<template>
    <div class="mb-5">
        <div class="container-fluid map-container">
            <div class="form center">
                <form class="form-inline locationzip">
                    <div class="form-group location-search-row">
                        <div class="col-12 col-lg-3 mb-3 mb-lg-0">
                            <h4>Search by City or Zip</h4>
                            <vue-autosuggest
                                :suggestions="getSuggestions()"
                                :input-props="{
                                    id: 'autosuggest__input',
                                    placeholder: 'ZIPCODE',
                                    class: 'form-control'
                                }"
                                v-model="currentZipcode"
                                @selected="onSuggestionClick"
                            >
                                <template slot-scope="{ suggestion }">
                                    <div class="my-suggestion-item">
                                        {{ suggestion.item }}
                                    </div>
                                </template>
                            </vue-autosuggest>
                        </div>
                        <div class="col-6 col-lg-3">
                            <h4>Distance</h4>
                            <select
                                name="miles"
                                class="form-control"
                                v-model="currentRadius"
                            >
                                <option value="10">10 Miles</option>
                                <option value="25">25 Miles</option>
                                <option value="40">40 Miles</option>
                                <option value="100">100 Miles</option>
                            </select>
                        </div>
                        <div class="col-6 col-lg-3">
                            <input
                                type="submit"
                                value="Search"
                                class="btn btn-danger"
                                @click.prevent="zipcodeOrRangeChanged"
                            />
                        </div>
                    </div>
                </form>
            </div>
            <div class="location-alert-container">
                <div
                    v-if="!filteredLocations.length && initialLoadComplete"
                    class="center"
                >
                    <div class="center alert alert-danger">
                        No locations found. Please adjust your search parameters
                        and try again
                    </div>
                </div>
            </div>
            <div class="location-results mt-3">
                <div class="col-sm-12 col-lg-4 px-4 py-4 bg-white locations">
                    <div class="d-flex justify-content-end pr-2 pt-1 pb-4">
                        <p class="margin-center">
                            <span class="text-info pr-1 font-weight-normal">{{
                                this.numLocations
                            }}</span>
                            Locations near
                            <span class="text-info px-1 font-weight-normal">{{
                                this.searchedZipcode
                            }}</span>
                        </p>
                    </div>
                    <div class="locations-list">
                        <div
                            class="col px-0 border-bottom-divider"
                            v-for="location in filteredLocations"
                        >
                            <div class="d-flex justify-content-between">
                                <h3 class="my-auto">{{ location.name }}</h3>
                                <a :href="location.directions_link" class="pr-3"
                                    ><img
                                        class="my-auto"
                                        src="/themes/rentone/assets/images/location-icon-png-4230.png"
                                        width="20"
                                        height="20"
                                /></a>
                            </div>
                            <p>
                                {{ location.address }}
                                {{ location.city }}, {{ location.state }}
                                {{ location.zipcode }}
                            </p>
                            <h4
                                class="d-flex justify-content-space-between max-height-30"
                            >
                                <a
                                    :href="
                                        telephoneLink(location.stripped_phone)
                                    "
                                    class="telephone"
                                    >{{ location.phone_number }}</a
                                >
                                <a
                                    :href="location.store_page"
                                    class="telephone pr-3"
                                    >View Store Page</a
                                >
                            </h4>
                            <!-- <a :href="location.store_page" role="button" class="btn btn-danger"
                            >View Store Page</a
                          > -->
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-lg-8 mb-4 mb-lg-0 map-col">
                    <google-map
                        :locations="this.filteredLocations"
                        :icon="icon"
                        :api-key="apiKey"
                    ></google-map>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import GoogleMap from "./GoogleMap.vue";
import { ServiceFactory } from "../../../../../shared/assets/js/services/ServiceFactory";
import { VueAutosuggest } from "vue-autosuggest";

const LocationsService = ServiceFactory.get("locations");

const LocationFilter = Object.freeze({
    ALL: "all",
    RETAILER: "retailer",
    STORE: "store"
});

export default {
    props: ["zipcode", "radius", "icon", "apiKey", "store"],

    components: {
        GoogleMap,
        VueAutosuggest
    },

    data() {
        return {
            filteredLocations: [],
            locations: [],
            currentZipcode: null,
            currentRadius: null,
            initialLoadComplete: false,
            isActive: false,
            locationFilter: LocationFilter.ALL,
            numLocations: 0,
            cityStateZips: {}
        };
    },

    created() {
        this.currentZipcode = this.zipcode;
        this.searchedZipcode = this.zipcode;
        this.currentRadius = this.radius;
        this.findLocations();
        this.getAutoCompleteList();
    },

    watch: {
        currentZipcode: function(zipcodeInput) {
            if (!this.cityStateZips[zipcodeInput]) {
                return;
            }
            this.findLocations();
        }
    },

    methods: {
        async findLocations() {
            const zipcodeInput = this.currentZipcode;
            const autoCompleteZip = this.cityStateZips[zipcodeInput];
            const zipToSearch = !!autoCompleteZip
                ? autoCompleteZip
                : this.attemptExtractingZipcodeFromAutoComplete(zipcodeInput); // This is for when first loading in, and we don't have the autocompletes to check against yet.
            const response = await LocationsService.get(
                zipToSearch,
                this.currentRadius,
                false
            );
            this.searchedZipcode = zipcodeInput;
            this.filteredLocations = response.data.locations;
            this.numLocations = this.filteredLocations.length;
            this.initialLoadComplete = true;
        },

        async getAutoCompleteList() {
            const response = await LocationsService.get("", 0, false);
            const allLocations = response.data.locations;
            const cityStateZipsNoDuplicates = allLocations.reduce(
                (acc, { city, state, zipcode }) => ({
                    ...acc,
                    [`${city}, ${state} (${zipcode})`]: zipcode
                }),
                {}
            );
            this.cityStateZips = cityStateZipsNoDuplicates;
        },

        attemptExtractingZipcodeFromAutoComplete(zipcodeInput) {
            const openParenthesis = zipcodeInput.indexOf("(");
            const closingParenthesis = zipcodeInput.indexOf(
                ")",
                openParenthesis
            );
            return closingParenthesis <= openParenthesis
                ? zipcodeInput
                : zipcodeInput.substring(
                      openParenthesis + 1,
                      closingParenthesis
                  );
        },

        async getAllLocations() {
            const response = await LocationsService.get("", 0, false);
            this.allLocations = response.data.locations;
        },

        getSuggestions() {
            const currentZipcode = this.currentZipcode.toLowerCase();

            if (currentZipcode.length < 3) {
                return [{ data: [] }];
            }
            const suggestions = Object.keys(this.cityStateZips).filter(key => {
                return key.toLowerCase().includes(currentZipcode);
            });

            return [{ data: suggestions }];
        },

        onSuggestionClick(autosuggestData) {
            if (!autosuggestData) {
                return;
            }
            this.currentZipcode = autosuggestData.item;
        },

        zipcodeOrRangeChanged() {
            this.updateUrl();
            this.findLocations();
        },

        updateUrl() {
            const url =
                location.protocol +
                "//" +
                location.host +
                location.pathname +
                "?zipcode=" +
                this.currentZipcode +
                "&radius=" +
                this.currentRadius +
                "&store=" +
                this.locationFilter;
            history.pushState("data to be passed", "Locations", url);
        },

        telephoneLink(stripped) {
            return "tel:" + stripped;
        }
    }
};
</script>
